/**
 * WelcomeScreen (Screen 1 / Hero)
 * 
 * Reimagined as the primary entry point for both new and returning users.
 * Replicates the polished AuthScreen UI but integrates into the onboarding flow.
 */
import React from 'react';
import { useOnboardingViewport } from '../shared/useViewport';

export const HeroScreen = ({ onGoogleSignIn, onNext, user }) => {
  const vp = useOnboardingViewport();
  const theme = 'light';
  const isDark = theme === 'dark';

  const colors = {
    background: isDark ? "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)" : "linear-gradient(180deg, #E6FFFA 0%, #F0FDFB 50%, #FFFFFF 100%)",
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#0F172A",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    border: isDark ? "rgba(255, 255, 255, 0.08)" : "#F1F5F9"
  };

  const primaryCTAStyle = {
    width: "100%",
    height: `${vp.spacing.ctaHeight}px`,
    background: "#14B8A6",
    borderRadius: `${vp.spacing.ctaBorderRadius}px`,
    border: "none",
    boxShadow: "0 8px 16px rgba(20, 184, 166, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "white",
    fontSize: `${vp.spacing.ctaFontSize}px`,
    fontWeight: "900",
    cursor: "pointer",
    transition: "transform 0.2s",
  };

  const isDesktop = vp.isTablet;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: isDesktop ? '480px' : '100%',
      margin: isDesktop ? '0 auto' : undefined,
      animation: 'fadeIn 0.8s ease-out',
      position: 'relative',
      background: 'transparent',
      justifyContent: 'space-between',
      boxSizing: 'border-box',
      transform: 'none',
      transformOrigin: 'top center'
    }}>
      {/* Background Decorative Icons */}
      <div style={{ position: "absolute", top: "40px", right: "40px", opacity: 0.1, fontSize: "24px", color: colors.text }}>⠿</div>
      <div style={{ position: "absolute", top: "120px", left: "20px", opacity: 0.1, fontSize: "20px", color: colors.text }}>📖</div>

      {/* Top Section Group: Illustration + Title */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Top Illustration — Compact for mobile */}
        <div style={{ width: "100%", textAlign: "center", paddingTop: `${vp.sp(10, 20, 30)}px`, position: "relative", zIndex: 1 }}>
          <img
            src="/images/login/brainhome.webp"
            alt="Vocab Master Illustration"
            style={{
              width: "75%",
              maxWidth: "240px",
              height: "auto",
              borderRadius: "24px",
              filter: isDark ? "brightness(0.9)" : "none",
              position: 'relative',
              zIndex: 2
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>

        {/* Title Section */}
        <div style={{ width: "100%", padding: `${vp.sp(8, 16, 24)}px 0`, textAlign: "center", zIndex: 2 }}>
          <h1 style={{
            fontSize: vp.sp(22, 26, 30),
            fontWeight: "900",
            color: colors.text,
            lineHeight: "1.1",
            marginBottom: "4px",
            letterSpacing: "-0.8px"
          }}>
            Master Vocabulary for<br />
            GMAT & GRE <span style={{ color: "#14B8A6", borderBottom: `3px solid rgba(20, 184, 166, 0.2)` }}>Faster</span>
          </h1>
          <p style={{ fontSize: "14.5px", color: colors.secondaryText, lineHeight: "1.5", maxWidth: "85%", margin: "0 auto", fontWeight: '500' }}>
            Learn words through fun games — not boring flashcards.
          </p>
        </div>
      </div>

      {/* Middle Section: Features Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: `${vp.sp(8, 10, 12)}px`,
        width: "100%",
        zIndex: 2
      }}>
        {/* Feature Cards */}
        {[
          { title: "Quick Sessions", subtitle: "5-minute rounds that actually move the needle.", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path></svg>, bg: "#F0F9FF" },
          { title: "Daily Streak", subtitle: "Play once a day, build an unbreakable habit.", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.256 1.149-3.149"></path></svg>, bg: "#FFF7ED" },
          { title: "Weak Words", subtitle: "Games auto-target words you keep getting wrong.", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>, bg: "#F5F3FF" },
          { title: "Install App", subtitle: "Add to your home screen for faster daily practice.", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>, bg: "#F0FDFA" }
        ].map((f, i) => (
          <div key={i} style={{
            background: colors.surface,
            padding: "12px",
            borderRadius: "22px",
            border: `1px solid ${colors.border}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
          }}>
            <div style={{ width: "36px", height: "36px", background: f.bg, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "8px" }}>
              {f.icon}
            </div>
            <div style={{ fontWeight: "700", fontSize: "13.5px", color: colors.text, marginBottom: "2px" }}>{f.title}</div>
            <div style={{ fontSize: "10.5px", color: colors.secondaryText, lineHeight: "1.2" }}>{f.subtitle}</div>
          </div>
        ))}
      </div>

      {/* Bottom Section: Auth & CTA Buttons */}
      <div style={{
        width: "100%",
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        zIndex: 10,
        marginTop: vp.sp(20, 0, 0)
      }}>
        {!user?.id && (
          <button
            style={{
              width: "100%",
              height: `${vp.sp(48, 54, 60)}px`,
              background: "white",
              borderRadius: "16px",
              border: "1px solid #F1F5F9",
              boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              cursor: "pointer"
            }}
            onClick={onGoogleSignIn}
          >
            <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            <span style={{ fontSize: "15.5px", fontWeight: "700", color: colors.text }}>Continue with Google</span>
          </button>
        )}

        <button
          style={primaryCTAStyle}
          onClick={onNext}
        >
          Let's Get Started
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default HeroScreen;
